import { FlexBox, useIsMobile, useBreakpoint, Skeleton, useToasts, HintTooltip } from '@brg/ui';
import React, { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { Lang } from '@brg/api';
import { LayoutConst } from '../../../core';
import { getTooltipStyles } from 'shared/constants/tooptipStyles';
import { useTheme } from 'styled-components';
import {
  ActionsTitle,
  ActionsWrapper,
  MainTitle,
  ActionBottomButtons,
  ModalContainer
} from '../sendProjects/styles';
import { SendProjectButton } from '_competition/components/SendProjectButton';
import { AppTester } from 'supporting';
import { useTranslation } from 'react-i18next';
import { InputWrapper, ModalInnerContainer, SubTitle, DeleteButtonContainer, ContextLeftColumn, CanonicalDomainHeader, ContextTagsContainer } from './styles';
import { Input } from '@brg/input';
import { LangObject } from '@brg/api/dist/api-core/utils/lang-utils';
import { LangComponent } from 'shared/components/LangTitleBox/LangTitleBox';
import { TextAreaLengthCounter } from 'shared/pages/teams/components/TextAreaLengthCounter';
import { API, Endpoints, TagsAPI } from '@brg/api';
import { BTag } from 'core/components/bTag/bTag';
import { AddIcon } from 'shared/icons/AddIcon';
import { CanonicalDomainModal } from './CanonicalDomainModal';
import { ContextModal } from './ContextModal';
import { BasketDeleteIcon } from 'shared/pages/projectEdit/BasketDeleteIcon';
import { InfoIcon } from 'shared/components/InfoIcon/InfoIcon';
import { TagEditButton } from 'shared/components/tagAdminProjectsSearchBar/styles';

interface IPlatform {
  id: string;
  appName: string;
  context: string;
  url: string;
  entityTypes?: string[];
  entityTypesEduTags?: string[];
  entityTypesRequiredTags?: string[];
}

interface Context {
  id: string;
  mainDescription: { ru: string; en: string };
  additionalDescription: { ru: string; en: string };
  currentLang: 'ru' | 'en';
  currentLangAdditional: 'ru' | 'en';
  selectedApp: string;
  contextUrl?: string;
}

interface Props {
  name: string;
  description: LangObject[];
  secondName: string;
  secondDescription: LangObject[];
  tagId?: string;
  canonicalDomains?: string[];
  onSubmit: (first: LangObject[], second: LangObject[]) => void;
}

type LangCode = 'ru' | 'en';

export const TagDescriptionModal: FC<Props> = ({
  name,
  description,
  secondName,
  secondDescription,
  tagId,
  canonicalDomains = [],
  onSubmit
}) => {
  const { t, i18n } = useTranslation();
  const toasts = useToasts();
  const theme = useTheme();
  const isGeo = AppTester.isGeograffee();
  const isMobile = useIsMobile();
  const is744BP = useBreakpoint(LayoutConst.screen.s);

  const toObj = useCallback((arr: LangObject[]) => {
    const norm = arr.reduce<Record<string,string>>((acc, x) => {
      acc[x.lang.toLowerCase()] = x.name;
      return acc;
    }, {});
    return {
      ru: norm.ru ?? '',
      en: norm.en ?? ''
    };
  }, []);

  const [canonicalTags, setCanonicalTags] = useState<string[]>(
    canonicalDomains.filter(domain => domain && domain !== 'unknown')
  );

  const [contexts, setContexts] = useState<Context[]>([]);
  const [availableContexts, setAvailableContexts] = useState<IPlatform[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isCanonicalDomainModalOpen, setIsCanonicalDomainModalOpen] = useState(false);
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [currentContextId, setCurrentContextId] = useState<string>('');
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  const getContextUrl = useCallback((appName: string, context: string): string => {
    const foundContext = availableContexts.find(platform => 
      platform.appName === appName && platform.context === context
    );
    return foundContext ? foundContext.url : appName;
  }, [availableContexts]);

  const fetchApiData = useCallback(async () => {
    if (!tagId || isDataLoaded) {
      return;
    }
  
    try {
      // Загружаем данные контекстов
      const contextsResponse = await API.Json(TagsAPI.GetEntityTypesByContexts());
      const platforms = Array.isArray(contextsResponse) ? (contextsResponse as unknown as IPlatform[]) : [];
      
      const generalContext: IPlatform = {
        id: 'general',
        appName: 'general',
        context: 'general',
        url: 'Общее'
      };
      
      const allContexts = [generalContext, ...platforms];
      setAvailableContexts(allContexts);
      const response = await API.request()
        .endpoint(Endpoints.tagsMnePw)
        .url('/api/Tags/GetTag')
        .addQuery('idTag', tagId)
        .addQuery('isIgnoreCanonical', 'true')
        .get()
        .send();
      
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        return;
      }
      
      if (!data) {
        return;
      }
      
      if (data?.descriptions && Array.isArray(data.descriptions)) {
        const contextMap = new Map<string, Context>();
        let contextIndex = 0;
        
        const descriptionsByBlock = new Map<string, any[]>();
        
        const contextGroups = new Map<string, any[]>();
        
        data.descriptions.forEach((desc: any) => {
          let contextKey = 'general:general';
          let contextsArray: any[] = [];
          
          if (desc.contexts && desc.contexts.length > 0) {
            contextsArray = desc.contexts;
            
            if (contextsArray.length === 1) {
              const context = contextsArray[0];
              contextKey = `${context.appName}:${context.context || context.appName}`;
            } else {
              contextKey = contextsArray.map(ctx => `${ctx.appName}:${ctx.context}`).join('|');
            }
          }
          
          if (!contextGroups.has(contextKey)) {
            contextGroups.set(contextKey, []);
          }
          
          contextGroups.get(contextKey)!.push(desc);
        });
        
        contextGroups.forEach((descriptions, contextKey) => {
          const blockKey = `${contextKey}_${contextIndex}`;
          descriptionsByBlock.set(blockKey, descriptions);
          contextIndex++;
        });
        
        descriptionsByBlock.forEach((descriptions, blockKey) => {
          const [contextKey] = blockKey.split('_');
          const blockIndex = parseInt(blockKey.split('_')[1]);
          
          const firstDescription = descriptions[0];
          const contextId = firstDescription?.contextId || `context-${blockIndex}`;
          
          const selectedApp = (() => {
            if (contextKey === 'general:general') return 'general:general';
            
            for (const desc of descriptions) {
              if (desc.selectedApp) {
                return desc.selectedApp;
              }
            }
            
            const firstDesc = descriptions[0];
            if (firstDesc.contexts && firstDesc.contexts.length > 0) {
              if (firstDesc.contexts.length === 1) {
                const ctx = firstDesc.contexts[0];
                return `${ctx.appName}:${ctx.context}`;
              } else {
                return JSON.stringify(firstDesc.contexts.map((ctx: any) => ({
                  appName: ctx.appName,
                  context: ctx.context,
                  url: ctx.url || ctx.appName
                })));
              }
            }
            return contextKey;
          })();
          
          const contextUrl = (() => {
            if (contextKey === 'general:general') return 'Общее';
            
            try {
              const selectedAppData = JSON.parse(selectedApp);
              if (Array.isArray(selectedAppData)) {
                const urls = selectedAppData.map((ctx: any) => {
                  return getContextUrl(ctx.appName, ctx.context);
                });
                return urls.join(', ');
              }
            } catch (e) {
            }
            
            const firstDesc = descriptions[0];
            
            if (firstDesc.contexts && firstDesc.contexts.length > 0) {
              if (firstDesc.contexts.length === 1) {
                const ctx = firstDesc.contexts[0];
                return getContextUrl(ctx.appName, ctx.context);
              } else {
                const urls = firstDesc.contexts.map((ctx: any) => {
                  return getContextUrl(ctx.appName, ctx.context);
                });
                return urls.join(', ');
              }
            }
            return selectedApp;
          })();
          
          const newContext: Context = {
            id: contextId,
            mainDescription: { ru: '', en: '' },
            additionalDescription: { ru: '', en: '' },
            currentLang: i18n.language.toLowerCase() as 'ru' | 'en',
            currentLangAdditional: i18n.language.toLowerCase() as 'ru' | 'en',
            selectedApp,
            contextUrl
          };
          
          descriptions.forEach((desc: any) => {
            if (desc.description && Array.isArray(desc.description)) {
              const descriptionObj: { ru: string, en: string } = { ru: '', en: '' };
              desc.description.forEach((item: any) => {
                if (item.lang && item.name) {
                  const lang = item.lang.toLowerCase();
                  if (lang === 'ru' || lang === 'en') {
                    descriptionObj[lang as 'ru' | 'en'] = item.name;
                  }
                }
              });
              
              if (desc.position === 0) {
                newContext.mainDescription = descriptionObj;
              } else if (desc.position === 1) {
                newContext.additionalDescription = descriptionObj;
              }
            }
          });
          
          contextMap.set(blockKey, newContext);
        });
        
        const newContexts = Array.from(contextMap.values());
        setContexts(newContexts);
      }
      
      if (data?.description && !data?.descriptions) {
        const generalContext: Context = {
          id: 'general-context',
          mainDescription: toObj(data.description),
          additionalDescription: { ru: '', en: '' },
          currentLang: i18n.language.toLowerCase() as 'ru' | 'en',
          currentLangAdditional: i18n.language.toLowerCase() as 'ru' | 'en',
          selectedApp: 'general:general',
          contextUrl: 'Общее'
        };
        
        setContexts([generalContext]);
      }
      
      if (data?.canonicalInfo?.contextInfos) {
        const canonicalDomains = data.canonicalInfo.contextInfos
          .map((ctx: any) => ctx.domain || ctx.appName)
          .filter((domain: string) => domain && domain !== 'unknown');
        setCanonicalTags(canonicalDomains);
      }
      
      setIsDataLoaded(true);
    } catch (error) {
      toasts.error(t('common.error.loading_data'));
    }
  }, [tagId, toObj, toasts, t, isDataLoaded, getContextUrl]);

  useEffect(() => {
    fetchApiData();
  }, [fetchApiData]);

  useEffect(() => {
    setIsDataLoaded(false);
    setContexts([]);
    setCanonicalTags(canonicalDomains.filter(domain => domain && domain !== 'unknown'));
  }, [tagId, canonicalDomains]);

  useEffect(() => {
    if (isDataLoaded && contexts.length > 0) {
      setContexts(prev => {
        return prev.map(ctx => ({
          ...ctx,
          currentLang: i18n.language.toLowerCase() as 'ru' | 'en',
          currentLangAdditional: i18n.language.toLowerCase() as 'ru' | 'en'
        }));
      });
    }
  }, [i18n.language, isDataLoaded, contexts.length]);

  const addContext = useCallback(() => {
    const newContext: Context = {
      id: Date.now().toString(),
      mainDescription: { ru: '', en: '' },
      additionalDescription: { ru: '', en: '' },
      currentLang: i18n.language.toLowerCase() as 'ru' | 'en',
      currentLangAdditional: i18n.language.toLowerCase() as 'ru' | 'en',
      selectedApp: '',
      contextUrl: ''
    };
    setContexts(prev => [...prev, newContext]);
  }, [i18n.language]);

  const removeContext = useCallback((contextId: string) => {
    const confirmed = window.confirm(t('project.confirmation_msg.delete_context'));
    if (confirmed) {
      setContexts(prev => prev.filter(ctx => ctx.id !== contextId));
    }
  }, [t]);

  const updateContext = useCallback((contextId: string, field: keyof Context, value: any) => {
    setContexts(prev => {
      return prev.map(ctx => {
        if (ctx.id === contextId) {
          return { ...ctx, [field]: value };
        }
        return ctx;
      });
    });
  }, []);

  const updateContextDescription = useCallback((contextId: string, lang: 'ru' | 'en', value: string) => {
    setContexts(prev => {
      return prev.map(ctx => {
        if (ctx.id === contextId) {
          return { 
            ...ctx, 
            mainDescription: { 
              ...ctx.mainDescription, 
              [lang]: value 
            } 
          };
        }
        return ctx;
      });
    });
  }, []);

  const updateContextAdditionalDescription = useCallback((contextId: string, lang: 'ru' | 'en', value: string) => {
    setContexts(prev => {
      return prev.map(ctx => {
        if (ctx.id === contextId) {
          return { 
            ...ctx, 
            additionalDescription: { 
              ...ctx.additionalDescription, 
              [lang]: value 
            } 
          };
        }
        return ctx;
      });
    });
  }, []);

  const toggleDropdown = useCallback((contextId: string) => {
    setOpenDropdowns(prev => ({ ...prev, [contextId]: !prev[contextId] }));
  }, []);

  const selectApp = useCallback((contextId: string, appValue: string) => {
    updateContext(contextId, 'selectedApp', appValue);
    toggleDropdown(contextId);
  }, [updateContext, toggleDropdown]);

  const removeCanonicalTag = useCallback((tagToRemove: string) => {
    setCanonicalTags(prev => prev.filter(tag => tag !== tagToRemove));
  }, []);

  const addCanonicalTag = useCallback(() => {
    setIsCanonicalDomainModalOpen(true);
  }, []);

  const handleCanonicalDomainsSave = useCallback((domains: string[]) => {
    setCanonicalTags(domains);
  }, []);

  const handleContextsSave = useCallback((selectedContexts: { appName: string; context: string; url: string }[]) => {
    if (!currentContextId) return;
    
    if (selectedContexts.length === 0) {
      updateContext(currentContextId, 'selectedApp', '');
      updateContext(currentContextId, 'contextUrl', '');
      setCurrentContextId('');
      return;
    }
    
    const usedContexts = contexts
      .filter(ctx => ctx.id !== currentContextId)
      .map(ctx => {
        if (!ctx.selectedApp) return [];
        
        try {
          const parsed = JSON.parse(ctx.selectedApp);
          if (Array.isArray(parsed)) {
            return parsed.map((c: any) => `${c.appName}:${c.context}`);
          }
        } catch {
          return [ctx.selectedApp];
        }
        return [ctx.selectedApp];
      })
      .flat();
    
    for (const selectedContext of selectedContexts) {
      const contextValue = `${selectedContext.appName}:${selectedContext.context}`;
      
      if (usedContexts.includes(contextValue)) {
        toasts.error(t('tags_modal.tag_description.context_already_used_error', { url: selectedContext.url }));
        return;
      }
    }
    
    if (selectedContexts.length === 1) {
      const selectedContext = selectedContexts[0];
      const contextValue = `${selectedContext.appName}:${selectedContext.context}`;
      
      updateContext(currentContextId, 'selectedApp', contextValue);
      updateContext(currentContextId, 'contextUrl', getContextUrl(selectedContext.appName, selectedContext.context));
    } else {
      const contextsData = selectedContexts.map(context => ({
        appName: context.appName,
        context: context.context,
        url: context.url
      }));
      
      updateContext(currentContextId, 'selectedApp', JSON.stringify(contextsData));
      updateContext(currentContextId, 'contextUrl', selectedContexts.map(c => getContextUrl(c.appName, c.context)).join(', '));
    }
    
    setCurrentContextId('');
  }, [currentContextId, contexts, toasts, updateContext, getContextUrl]);

  const handleSave = async () => {
    for (const context of contexts) {
      if (!context.selectedApp || context.selectedApp.trim() === '') {
        toasts.error(t('tags_modal.tag_description.select_context_error'));
        return;
      }
    }

    for (const context of contexts) {
      const hasMainDesc = !!context.mainDescription.ru.trim() || !!context.mainDescription.en.trim();
      const hasAdditionalDesc = !!context.additionalDescription.ru.trim() || !!context.additionalDescription.en.trim();
      
      if (hasMainDesc && (!context.mainDescription.ru.trim() || !context.mainDescription.en.trim())) {
        toasts.error(t('tags_modal.tag_description.fill_main_description_error'));
        return;
      }
      
      if (hasAdditionalDesc && (!context.additionalDescription.ru.trim() || !context.additionalDescription.en.trim())) {
        toasts.error(t('tags_modal.tag_description.fill_additional_description_error'));
        return;
      }
    }

    const descriptions: any[] = [];
    
    contexts.forEach((context, index) => {
      const hasMainDesc = !!context.mainDescription.ru.trim() || !!context.mainDescription.en.trim();
      const hasAdditionalDesc = !!context.additionalDescription.ru.trim() || !!context.additionalDescription.en.trim();
      
      const isGeneralContext = context.selectedApp === 'general:general';
      const descriptionType = isGeneralContext ? 0 : 1;
      
      let contextsArray: any[] = [];
      
      try {
        const parsed = JSON.parse(context.selectedApp);
        if (Array.isArray(parsed) && parsed.length > 0) {
          contextsArray = parsed.map((ctx: any) => ({
            appName: ctx.appName || 'general',
            context: ctx.context || 'general',
            url: ctx.url || ctx.appName
          }));
        } else {
          contextsArray = [{
            appName: parsed.appName || 'general',
            context: parsed.context || 'general',
            url: parsed.url || parsed.appName
          }];
        }
      } catch {
        const [appName, contextValue] = context.selectedApp.includes(':') 
          ? context.selectedApp.split(':') 
          : [context.selectedApp, context.selectedApp];
        contextsArray = [{
          appName: appName || 'general',
          context: contextValue || 'general',
          url: context.contextUrl || appName
        }];
      }
      
      if (hasMainDesc) {
        const mainDescriptionObj: any = {
          descriptionType: descriptionType,
          position: 0,
          description: [
            { lang: 'ru', name: context.mainDescription.ru || '' },
            { lang: 'en', name: context.mainDescription.en || '' }
          ].filter(desc => desc.name && desc.name.trim() !== ''),
          dateCreate: new Date().toISOString(),
          dateUpdate: new Date().toISOString(),
          contextId: context.id
        };
        
        if (descriptionType === 1) {
          mainDescriptionObj.contexts = contextsArray;
        }
        
        descriptions.push(mainDescriptionObj);
      }
      
      if (hasAdditionalDesc) {
        const additionalDescriptionObj: any = {
          descriptionType: descriptionType,
          position: 1,
          description: [
            { lang: 'ru', name: context.additionalDescription.ru || '' },
            { lang: 'en', name: context.additionalDescription.en || '' }
          ].filter(desc => desc.name && desc.name.trim() !== ''),
          dateCreate: new Date().toISOString(),
          dateUpdate: new Date().toISOString(),
          contextId: context.id
        };
        
        if (descriptionType === 1) {
          additionalDescriptionObj.contexts = contextsArray;
        }
        
        descriptions.push(additionalDescriptionObj);
      }
      
      if (!hasMainDesc && !hasAdditionalDesc) {
        const emptyMainDescriptionObj: any = {
          descriptionType: descriptionType,
          position: 0,
          description: [],
          dateCreate: new Date().toISOString(),
          dateUpdate: new Date().toISOString(),
          contextId: context.id
        };
        
        if (descriptionType === 1) {
          emptyMainDescriptionObj.contexts = contextsArray;
        }
        
        descriptions.push(emptyMainDescriptionObj);
        
        const emptyAdditionalDescriptionObj: any = {
          descriptionType: descriptionType,
          position: 1,
          description: [],
          dateCreate: new Date().toISOString(),
          dateUpdate: new Date().toISOString(),
          contextId: context.id
        };
        
        if (descriptionType === 1) {
          emptyAdditionalDescriptionObj.contexts = contextsArray;
        }
        
        descriptions.push(emptyAdditionalDescriptionObj);
      }
    });

    const canonicalInfo = {
      isCanonical: canonicalTags.length > 0,
      contextInfos: canonicalTags.map(domain => ({
        domain: domain || '',
        canonicalType: 0
      }))
    };


    const payload = {
      tagId: tagId || '',
      canonicalInfo,
      descriptions: contexts.length > 0 ? descriptions : []
    };

    try {
      const response = await API.request()
        .endpoint(Endpoints.tagsMnePw)
        .url('/api/Tags/UpdateCanonical')
        .addQuery('tagId', tagId || '')
        .post()
        .body(payload)
        .send();
      
      if (response?.status === 200) {
        toasts.info(t('common.success.data_saved'));
      } else {
        toasts.error(t('common.error.saving_data'));
      }
    } catch (error) {
      toasts.error(t('common.error.saving_data'));
    }
  };

  return (
    <>
    <ModalContainer style={{ overflow: 'auto', height: '100%' }} justify="flex-start" offsetTop={88} offsetTopMobile={1}>
      <ModalInnerContainer
        width="100%"
        directionMobile="column"
        justify={isMobile ? 'flex-start' : 'center'}
        align={isMobile ? 'center' : 'flex-start'}
        style={{ position: 'relative' }}
      >
        <ActionsWrapper width="100%" direction="column" style={{ justifyContent: 'center', position: 'relative' }}>
          <ActionsTitle justify="space-between" justifyMobile="flex-start" direction="row">
            <FlexBox direction="column">
              <MainTitle>{name || <Skeleton width={100} />}</MainTitle>
            </FlexBox>
            {!is744BP && (
              <FlexBox>
                <SendProjectButton isGreen={isGeo} width={187} onClick={handleSave}>
                  {t('competition.save')}
                </SendProjectButton>
              </FlexBox>
            )}
          </ActionsTitle>

          <FlexBox width="100%" gx={24} style={{ marginTop: 24 }}>
            <CanonicalDomainHeader>
              <SubTitle>{t('tags_modal.tag_description.canonical_domain')}</SubTitle>
              <div style={{marginLeft: 8}}>
                <InfoIcon msg={t('tags_modal.tag_description.canonical_domain_hint')} stc/>
              </div>
            </CanonicalDomainHeader>
            <FlexBox width="100%" direction="row" gx={12} align="center" style={{ flexWrap: 'wrap', gap: '8px' }}>
              {canonicalTags.map((tag) => (
                <HintTooltip
                  key={tag}
                  text={tag.length > 24 ? tag : ''}
                  customStyle={getTooltipStyles(theme)}
                  z={6}
                  noDisappearDelay
                  positionY="top"
                  onWhiteBg
                >
                  <BTag
                    showRemoveIcon
                    onIconClick={() => removeCanonicalTag(tag)}
                  >
                    {tag}
                  </BTag>
                </HintTooltip>
              ))}
              <BTag
                onClick={addCanonicalTag}
              >
                <AddIcon />
              </BTag>
            </FlexBox>
          </FlexBox>

          {contexts.map((context) => (
            <FlexBox key={context.id} width="100%" offsetTop={32}  directionMobile="column" style={{borderBottom: '1px solid #BFBFBF', paddingBottom: 32, columnGap: 24}}>
              <ContextLeftColumn>
                <SubTitle style={{marginTop: 6}}>{t('tags_modal.tag_description.context')}</SubTitle>
                <ContextTagsContainer>
                  {context.selectedApp && context.selectedApp.trim() !== '' ? (
                    (() => {
                      let contextsToShow: string[] = [];
                      
                      if (context.selectedApp === 'general:general') {
                        contextsToShow = ['Общее'];
                      } else {
                        try {
                          const parsed = JSON.parse(context.selectedApp);
                          if (Array.isArray(parsed)) {
                            contextsToShow = parsed.map((ctx: any) => {
                              return getContextUrl(ctx.appName, ctx.context);
                            });
                          } else {
                            contextsToShow = [context.contextUrl || 'Общее'];
                          }
                        } catch {
                          contextsToShow = [context.contextUrl || 'Общее'];
                        }
                      }
                      
                      return contextsToShow.map((contextName, index) => (
                        <HintTooltip
                          key={`${context.id}-${index}`}
                          text={contextName.length > 24 ? contextName : ''}
                          customStyle={getTooltipStyles(theme)}
                          z={6}
                          noDisappearDelay
                          positionY="top"
                          onWhiteBg
                        >
                          <BTag
                            showRemoveIcon
                            onIconClick={() => {
                              if (contextsToShow.length === 1) {
                                updateContext(context.id, 'selectedApp', '');
                                updateContext(context.id, 'contextUrl', '');
                              } else {
                                try {
                                  const parsed = JSON.parse(context.selectedApp);
                                  if (Array.isArray(parsed)) {
                                    const updatedContexts = parsed.filter((ctx: any, ctxIndex: number) => ctxIndex !== index);
                                    if (updatedContexts.length === 0) {
                                      updateContext(context.id, 'selectedApp', '');
                                      updateContext(context.id, 'contextUrl', '');
                                    } else if (updatedContexts.length === 1) {
                                      const ctx = updatedContexts[0];
                                      updateContext(context.id, 'selectedApp', `${ctx.appName}:${ctx.context}`);
                                      updateContext(context.id, 'contextUrl', getContextUrl(ctx.appName, ctx.context));
                                    } else {
                                      updateContext(context.id, 'selectedApp', JSON.stringify(updatedContexts));
                                      updateContext(context.id, 'contextUrl', updatedContexts.map((ctx: any) => getContextUrl(ctx.appName, ctx.context)).join(', '));
                                    }
                                  }
                                } catch {
                                  updateContext(context.id, 'selectedApp', '');
                                  updateContext(context.id, 'contextUrl', '');
                                }
                              }
                            }}
                          >
                            {contextName}
                          </BTag>
                        </HintTooltip>
                      ));
                    })()
                  ) : null}
                    <BTag onClick={() => {
                      setCurrentContextId(context.id);
                      setIsContextModalOpen(true);
                    }} >
                      <AddIcon />
                    </BTag>
                </ContextTagsContainer>
                <DeleteButtonContainer>  
                  <HintTooltip 
                    customStyle={getTooltipStyles(theme)} 
                    text={t('project.hints.delete_context')} 
                    z={6} 
                    noDisappearDelay 
                    positionY="top"
                    onWhiteBg
                  >
                    <BasketDeleteIcon onClick={() => removeContext(context.id)} fill="#909090"/>
                  </HintTooltip>
                </DeleteButtonContainer>
              </ContextLeftColumn>

              <FlexBox width="100%" direction="column" gy={24} style={{ flex: 1 }}>
                <InputWrapper width="100%" direction="column" gy={12} style={{ position: 'relative' }}>
                  <FlexBox width="100%" justify="space-between" align="center">
                    <SubTitle>{t('tags_modal.tag_description.main_description')}</SubTitle>
                    <LangComponent lang={context.currentLang} onLangChange={() => updateContext(context.id, 'currentLang', context.currentLang === 'ru' ? 'en' : 'ru')} />
              </FlexBox>
              <Input.Textarea
                      value={context.mainDescription[context.currentLang]}
                      onChange={e => updateContextDescription(context.id, context.currentLang, (e.target as any).value)}
                maxLength={500}
                      placeholder={t('tags_modal.tag_description.enter_description')}
              />
              <TextAreaLengthCounter
                      value={context.mainDescription[context.currentLang].length}
                pos={{ bottom: 8, right: isMobile ? 16 : 8 }}
                maxLength={500}
              />
            </InputWrapper>

                  <InputWrapper width="100%" direction="column" gy={12} style={{ position: 'relative' }}>
              <FlexBox width="100%" justify="space-between" align="center">
                      <SubTitle>{t('tags_modal.tag_description.additional_description')}</SubTitle>
                      <LangComponent 
                        lang={context.currentLangAdditional} 
                        onLangChange={() => updateContext(context.id, 'currentLangAdditional', context.currentLangAdditional === 'ru' ? 'en' : 'ru')} 
                      />
              </FlexBox>
              <Input.Textarea
                      value={context.additionalDescription[context.currentLangAdditional]}
                      onChange={e => updateContextAdditionalDescription(context.id, context.currentLangAdditional, (e.target as any).value)}
                maxLength={1500}
                      placeholder={t('tags_modal.tag_description.enter_additional_description')}
              />
              <TextAreaLengthCounter
                      value={context.additionalDescription[context.currentLangAdditional].length}
                pos={{ bottom: 8, right: isMobile ? 16 : 8 }}
                maxLength={1500}
              />
              </InputWrapper>
                </FlexBox>
            </FlexBox>
          ))}

          <FlexBox width="100%" align="center" style={{ marginTop: 24, marginBottom: 56 }}>
            <TagEditButton
              width={278}
              widthMobile={211}
              onClick={addContext}
              style={{columnGap: 8}}
            >
              <AddIcon fill={theme.tags.tagEditModal.buttons.edit.color}/>
              {t('tags_modal.tag_description.add_context')}
            </TagEditButton>
          </FlexBox>

          {is744BP && (
            <ActionBottomButtons>
              <SendProjectButton isGreen={isGeo} width={187} onClick={handleSave}>
                {t('competition.save')}
              </SendProjectButton>
            </ActionBottomButtons>
          )}
        </ActionsWrapper>
      </ModalInnerContainer>
    </ModalContainer>

    {isCanonicalDomainModalOpen && (
      <CanonicalDomainModal
        onClose={() => setIsCanonicalDomainModalOpen(false)}
        onSave={handleCanonicalDomainsSave}
        selectedDomains={canonicalTags}
      />
    )}

    {isContextModalOpen && (
      <ContextModal
        onClose={() => {
          setIsContextModalOpen(false);
          setCurrentContextId('');
        }}
        onSave={handleContextsSave}
        availableContexts={availableContexts}
        selectedContexts={(() => {
          if (!currentContextId) return [];
          
          const currentContext = contexts.find(ctx => ctx.id === currentContextId);
          if (!currentContext || !currentContext.selectedApp) return [];
          
          try {
            const parsed = JSON.parse(currentContext.selectedApp);
            if (Array.isArray(parsed)) {
              return parsed.map((ctx: any) => ({
                appName: ctx.appName || 'general',
                context: ctx.context || 'general',
                url: ctx.url || ctx.appName
              }));
            } else {
              return [{
                appName: parsed.appName || 'general',
                context: parsed.context || 'general',
                url: parsed.url || parsed.appName
              }];
            }
          } catch {
            const [appName, contextValue] = currentContext.selectedApp.includes(':') 
              ? currentContext.selectedApp.split(':') 
              : [currentContext.selectedApp, currentContext.selectedApp];
            const url = currentContext.contextUrl || currentContext.selectedApp;
            
            return [{
              appName: appName,
              context: contextValue,
              url: url
            }];
          }
        })()}
        usedContexts={(() => {
          return contexts
            .filter(ctx => ctx.id !== currentContextId)
            .map(ctx => {
              if (!ctx.selectedApp) return [];
              
              try {
                const parsed = JSON.parse(ctx.selectedApp);
                if (Array.isArray(parsed)) {
                  return parsed.map((c: any) => `${c.appName}:${c.context}`);
                }
              } catch {
                return [ctx.selectedApp];
              }
              return [ctx.selectedApp];
            })
            .flat();
        })()}
      />
    )}
    </>
  );
};
import { FlexBox, useIsMobile, useBreakpoint, Skeleton, useToasts, HintTooltip } from '@brg/ui';
import React, { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { Lang } from '@brg/api';
import { LayoutConst } from '../../../core';
import { getTooltipStyles } from 'shared/constants/tooptipStyles';
import { useTheme } from 'styled-components';
import {
  ActionsTitle,
  ActionsWrapper,
  MainTitle,
  ActionBottomButtons,
  ModalContainer
} from '../sendProjects/styles';
import { SendProjectButton } from '_competition/components/SendProjectButton';
import { AppTester } from 'supporting';
import { useTranslation } from 'react-i18next';
import { InputWrapper, ModalInnerContainer, SubTitle, DeleteButtonContainer, ContextLeftColumn, CanonicalDomainHeader, ContextTagsContainer } from './styles';
import { Input } from '@brg/input';
import { LangObject } from '@brg/api/dist/api-core/utils/lang-utils';
import { LangComponent } from 'shared/components/LangTitleBox/LangTitleBox';
import { TextAreaLengthCounter } from 'shared/pages/teams/components/TextAreaLengthCounter';
import { API, Endpoints, TagsAPI } from '@brg/api';
import { BTag } from 'core/components/bTag/bTag';
import { AddIcon } from 'shared/icons/AddIcon';
import { CanonicalDomainModal } from './CanonicalDomainModal';
import { ContextModal } from './ContextModal';
import { BasketDeleteIcon } from 'shared/pages/projectEdit/BasketDeleteIcon';
import { InfoIcon } from 'shared/components/InfoIcon/InfoIcon';
import { TagEditButton } from 'shared/components/tagAdminProjectsSearchBar/styles';

interface IPlatform {
  id: string;
  appName: string;
  context: string;
  url: string;
  entityTypes?: string[];
  entityTypesEduTags?: string[];
  entityTypesRequiredTags?: string[];
}

interface Context {
  id: string;
  mainDescription: { ru: string; en: string };
  additionalDescription: { ru: string; en: string };
  currentLang: 'ru' | 'en';
  currentLangAdditional: 'ru' | 'en';
  selectedApp: string;
  contextUrl?: string;
}

interface Props {
  name: string;
  description: LangObject[];
  secondName: string;
  secondDescription: LangObject[];
  tagId?: string;
  canonicalDomains?: string[];
  onSubmit: (first: LangObject[], second: LangObject[]) => void;
}

type LangCode = 'ru' | 'en';

export const TagDescriptionModal: FC<Props> = ({
  name,
  description,
  secondName,
  secondDescription,
  tagId,
  canonicalDomains = [],
  onSubmit
}) => {
  const { t, i18n } = useTranslation();
  const toasts = useToasts();
  const theme = useTheme();
  const isGeo = AppTester.isGeograffee();
  const isMobile = useIsMobile();
  const is744BP = useBreakpoint(LayoutConst.screen.s);

  const toObj = useCallback((arr: LangObject[]) => {
    const norm = arr.reduce<Record<string,string>>((acc, x) => {
      acc[x.lang.toLowerCase()] = x.name;
      return acc;
    }, {});
    return {
      ru: norm.ru ?? '',
      en: norm.en ?? ''
    };
  }, []);

  const [canonicalTags, setCanonicalTags] = useState<string[]>(
    canonicalDomains.filter(domain => domain && domain !== 'unknown')
  );

  const [contexts, setContexts] = useState<Context[]>([]);
  const [availableContexts, setAvailableContexts] = useState<IPlatform[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isCanonicalDomainModalOpen, setIsCanonicalDomainModalOpen] = useState(false);
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [currentContextId, setCurrentContextId] = useState<string>('');
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  const getContextUrl = useCallback((appName: string, context: string): string => {
    const foundContext = availableContexts.find(platform => 
      platform.appName === appName && platform.context === context
    );
    return foundContext ? foundContext.url : appName;
  }, [availableContexts]);

  const fetchApiData = useCallback(async () => {
    if (!tagId || isDataLoaded) {
      return;
    }
  
    try {
      // Загружаем данные контекстов
      const contextsResponse = await API.Json(TagsAPI.GetEntityTypesByContexts());
      const platforms = Array.isArray(contextsResponse) ? (contextsResponse as unknown as IPlatform[]) : [];
      
      const generalContext: IPlatform = {
        id: 'general',
        appName: 'general',
        context: 'general',
        url: 'Общее'
      };
      
      const allContexts = [generalContext, ...platforms];
      setAvailableContexts(allContexts);
      const response = await API.request()
        .endpoint(Endpoints.tagsMnePw)
        .url('/api/Tags/GetTag')
        .addQuery('idTag', tagId)
        .addQuery('isIgnoreCanonical', 'true')
        .get()
        .send();
      
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        return;
      }
      
      if (!data) {
        return;
      }
      
      if (data?.descriptions && Array.isArray(data.descriptions)) {
        const contextMap = new Map<string, Context>();
        let contextIndex = 0;
        
        const descriptionsByBlock = new Map<string, any[]>();
        
        const contextGroups = new Map<string, any[]>();
        
        data.descriptions.forEach((desc: any) => {
          let contextKey = 'general:general';
          let contextsArray: any[] = [];
          
          if (desc.contexts && desc.contexts.length > 0) {
            contextsArray = desc.contexts;
            
            if (contextsArray.length === 1) {
              const context = contextsArray[0];
              contextKey = `${context.appName}:${context.context || context.appName}`;
            } else {
              contextKey = contextsArray.map(ctx => `${ctx.appName}:${ctx.context}`).join('|');
            }
          }
          
          if (!contextGroups.has(contextKey)) {
            contextGroups.set(contextKey, []);
          }
          
          contextGroups.get(contextKey)!.push(desc);
        });
        
        contextGroups.forEach((descriptions, contextKey) => {
          const blockKey = `${contextKey}_${contextIndex}`;
          descriptionsByBlock.set(blockKey, descriptions);
          contextIndex++;
        });
        
        descriptionsByBlock.forEach((descriptions, blockKey) => {
          const [contextKey] = blockKey.split('_');
          const blockIndex = parseInt(blockKey.split('_')[1]);
          
          const firstDescription = descriptions[0];
          const contextId = firstDescription?.contextId || `context-${blockIndex}`;
          
          const selectedApp = (() => {
            if (contextKey === 'general:general') return 'general:general';
            
            for (const desc of descriptions) {
              if (desc.selectedApp) {
                return desc.selectedApp;
              }
            }
            
            const firstDesc = descriptions[0];
            if (firstDesc.contexts && firstDesc.contexts.length > 0) {
              if (firstDesc.contexts.length === 1) {
                const ctx = firstDesc.contexts[0];
                return `${ctx.appName}:${ctx.context}`;
              } else {
                return JSON.stringify(firstDesc.contexts.map((ctx: any) => ({
                  appName: ctx.appName,
                  context: ctx.context,
                  url: ctx.url || ctx.appName
                })));
              }
            }
            return contextKey;
          })();
          
          const contextUrl = (() => {
            if (contextKey === 'general:general') return 'Общее';
            
            try {
              const selectedAppData = JSON.parse(selectedApp);
              if (Array.isArray(selectedAppData)) {
                const urls = selectedAppData.map((ctx: any) => {
                  return getContextUrl(ctx.appName, ctx.context);
                });
                return urls.join(', ');
              }
            } catch (e) {
            }
            
            const firstDesc = descriptions[0];
            
            if (firstDesc.contexts && firstDesc.contexts.length > 0) {
              if (firstDesc.contexts.length === 1) {
                const ctx = firstDesc.contexts[0];
                return getContextUrl(ctx.appName, ctx.context);
              } else {
                const urls = firstDesc.contexts.map((ctx: any) => {
                  return getContextUrl(ctx.appName, ctx.context);
                });
                return urls.join(', ');
              }
            }
            return selectedApp;
          })();
          
          const newContext: Context = {
            id: contextId,
            mainDescription: { ru: '', en: '' },
            additionalDescription: { ru: '', en: '' },
            currentLang: i18n.language.toLowerCase() as 'ru' | 'en',
            currentLangAdditional: i18n.language.toLowerCase() as 'ru' | 'en',
            selectedApp,
            contextUrl
          };
          
          descriptions.forEach((desc: any) => {
            if (desc.description && Array.isArray(desc.description)) {
              const descriptionObj: { ru: string, en: string } = { ru: '', en: '' };
              desc.description.forEach((item: any) => {
                if (item.lang && item.name) {
                  const lang = item.lang.toLowerCase();
                  if (lang === 'ru' || lang === 'en') {
                    descriptionObj[lang as 'ru' | 'en'] = item.name;
                  }
                }
              });
              
              if (desc.position === 0) {
                newContext.mainDescription = descriptionObj;
              } else if (desc.position === 1) {
                newContext.additionalDescription = descriptionObj;
              }
            }
          });
          
          contextMap.set(blockKey, newContext);
        });
        
        const newContexts = Array.from(contextMap.values());
        setContexts(newContexts);
      }
      
      if (data?.description && !data?.descriptions) {
        const generalContext: Context = {
          id: 'general-context',
          mainDescription: toObj(data.description),
          additionalDescription: { ru: '', en: '' },
          currentLang: i18n.language.toLowerCase() as 'ru' | 'en',
          currentLangAdditional: i18n.language.toLowerCase() as 'ru' | 'en',
          selectedApp: 'general:general',
          contextUrl: 'Общее'
        };
        
        setContexts([generalContext]);
      }
      
      if (data?.canonicalInfo?.contextInfos) {
        const canonicalDomains = data.canonicalInfo.contextInfos
          .map((ctx: any) => ctx.domain || ctx.appName)
          .filter((domain: string) => domain && domain !== 'unknown');
        setCanonicalTags(canonicalDomains);
      }
      
      setIsDataLoaded(true);
    } catch (error) {
      toasts.error(t('common.error.loading_data'));
    }
  }, [tagId, toObj, toasts, t, isDataLoaded, getContextUrl]);

  useEffect(() => {
    fetchApiData();
  }, [fetchApiData]);

  useEffect(() => {
    setIsDataLoaded(false);
    setContexts([]);
    setCanonicalTags(canonicalDomains.filter(domain => domain && domain !== 'unknown'));
  }, [tagId, canonicalDomains]);

  useEffect(() => {
    if (isDataLoaded && contexts.length > 0) {
      setContexts(prev => {
        return prev.map(ctx => ({
          ...ctx,
          currentLang: i18n.language.toLowerCase() as 'ru' | 'en',
          currentLangAdditional: i18n.language.toLowerCase() as 'ru' | 'en'
        }));
      });
    }
  }, [i18n.language, isDataLoaded, contexts.length]);

  const addContext = useCallback(() => {
    const newContext: Context = {
      id: Date.now().toString(),
      mainDescription: { ru: '', en: '' },
      additionalDescription: { ru: '', en: '' },
      currentLang: i18n.language.toLowerCase() as 'ru' | 'en',
      currentLangAdditional: i18n.language.toLowerCase() as 'ru' | 'en',
      selectedApp: '',
      contextUrl: ''
    };
    setContexts(prev => [...prev, newContext]);
  }, [i18n.language]);

  const removeContext = useCallback((contextId: string) => {
    const confirmed = window.confirm(t('project.confirmation_msg.delete_context'));
    if (confirmed) {
      setContexts(prev => prev.filter(ctx => ctx.id !== contextId));
    }
  }, [t]);

  const updateContext = useCallback((contextId: string, field: keyof Context, value: any) => {
    setContexts(prev => {
      return prev.map(ctx => {
        if (ctx.id === contextId) {
          return { ...ctx, [field]: value };
        }
        return ctx;
      });
    });
  }, []);

  const updateContextDescription = useCallback((contextId: string, lang: 'ru' | 'en', value: string) => {
    setContexts(prev => {
      return prev.map(ctx => {
        if (ctx.id === contextId) {
          return { 
            ...ctx, 
            mainDescription: { 
              ...ctx.mainDescription, 
              [lang]: value 
            } 
          };
        }
        return ctx;
      });
    });
  }, []);

  const updateContextAdditionalDescription = useCallback((contextId: string, lang: 'ru' | 'en', value: string) => {
    setContexts(prev => {
      return prev.map(ctx => {
        if (ctx.id === contextId) {
          return { 
            ...ctx, 
            additionalDescription: { 
              ...ctx.additionalDescription, 
              [lang]: value 
            } 
          };
        }
        return ctx;
      });
    });
  }, []);

  const toggleDropdown = useCallback((contextId: string) => {
    setOpenDropdowns(prev => ({ ...prev, [contextId]: !prev[contextId] }));
  }, []);

  const selectApp = useCallback((contextId: string, appValue: string) => {
    updateContext(contextId, 'selectedApp', appValue);
    toggleDropdown(contextId);
  }, [updateContext, toggleDropdown]);

  const removeCanonicalTag = useCallback((tagToRemove: string) => {
    setCanonicalTags(prev => prev.filter(tag => tag !== tagToRemove));
  }, []);

  const addCanonicalTag = useCallback(() => {
    setIsCanonicalDomainModalOpen(true);
  }, []);

  const handleCanonicalDomainsSave = useCallback((domains: string[]) => {
    setCanonicalTags(domains);
  }, []);

  const handleContextsSave = useCallback((selectedContexts: { appName: string; context: string; url: string }[]) => {
    if (!currentContextId) return;
    
    if (selectedContexts.length === 0) {
      updateContext(currentContextId, 'selectedApp', '');
      updateContext(currentContextId, 'contextUrl', '');
      setCurrentContextId('');
      return;
    }
    
    const usedContexts = contexts
      .filter(ctx => ctx.id !== currentContextId)
      .map(ctx => {
        if (!ctx.selectedApp) return [];
        
        try {
          const parsed = JSON.parse(ctx.selectedApp);
          if (Array.isArray(parsed)) {
            return parsed.map((c: any) => `${c.appName}:${c.context}`);
          }
        } catch {
          return [ctx.selectedApp];
        }
        return [ctx.selectedApp];
      })
      .flat();
    
    for (const selectedContext of selectedContexts) {
      const contextValue = `${selectedContext.appName}:${selectedContext.context}`;
      
      if (usedContexts.includes(contextValue)) {
        toasts.error(t('tags_modal.tag_description.context_already_used_error', { url: selectedContext.url }));
        return;
      }
    }
    
    if (selectedContexts.length === 1) {
      const selectedContext = selectedContexts[0];
      const contextValue = `${selectedContext.appName}:${selectedContext.context}`;
      
      updateContext(currentContextId, 'selectedApp', contextValue);
      updateContext(currentContextId, 'contextUrl', getContextUrl(selectedContext.appName, selectedContext.context));
    } else {
      const contextsData = selectedContexts.map(context => ({
        appName: context.appName,
        context: context.context,
        url: context.url
      }));
      
      updateContext(currentContextId, 'selectedApp', JSON.stringify(contextsData));
      updateContext(currentContextId, 'contextUrl', selectedContexts.map(c => getContextUrl(c.appName, c.context)).join(', '));
    }
    
    setCurrentContextId('');
  }, [currentContextId, contexts, toasts, updateContext, getContextUrl]);

  const handleSave = async () => {
    for (const context of contexts) {
      if (!context.selectedApp || context.selectedApp.trim() === '') {
        toasts.error(t('tags_modal.tag_description.select_context_error'));
        return;
      }
    }

    for (const context of contexts) {
      const hasMainDesc = !!context.mainDescription.ru.trim() || !!context.mainDescription.en.trim();
      const hasAdditionalDesc = !!context.additionalDescription.ru.trim() || !!context.additionalDescription.en.trim();
      
      if (hasMainDesc && (!context.mainDescription.ru.trim() || !context.mainDescription.en.trim())) {
        toasts.error(t('tags_modal.tag_description.fill_main_description_error'));
        return;
      }
      
      if (hasAdditionalDesc && (!context.additionalDescription.ru.trim() || !context.additionalDescription.en.trim())) {
        toasts.error(t('tags_modal.tag_description.fill_additional_description_error'));
        return;
      }
    }

    const descriptions: any[] = [];
    
    contexts.forEach((context, index) => {
      const hasMainDesc = !!context.mainDescription.ru.trim() || !!context.mainDescription.en.trim();
      const hasAdditionalDesc = !!context.additionalDescription.ru.trim() || !!context.additionalDescription.en.trim();
      
      const isGeneralContext = context.selectedApp === 'general:general';
      const descriptionType = isGeneralContext ? 0 : 1;
      
      let contextsArray: any[] = [];
      
      try {
        const parsed = JSON.parse(context.selectedApp);
        if (Array.isArray(parsed) && parsed.length > 0) {
          contextsArray = parsed.map((ctx: any) => ({
            appName: ctx.appName || 'general',
            context: ctx.context || 'general',
            url: ctx.url || ctx.appName
          }));
        } else {
          contextsArray = [{
            appName: parsed.appName || 'general',
            context: parsed.context || 'general',
            url: parsed.url || parsed.appName
          }];
        }
      } catch {
        const [appName, contextValue] = context.selectedApp.includes(':') 
          ? context.selectedApp.split(':') 
          : [context.selectedApp, context.selectedApp];
        contextsArray = [{
          appName: appName || 'general',
          context: contextValue || 'general',
          url: context.contextUrl || appName
        }];
      }
      
      if (hasMainDesc) {
        const mainDescriptionObj: any = {
          descriptionType: descriptionType,
          position: 0,
          description: [
            { lang: 'ru', name: context.mainDescription.ru || '' },
            { lang: 'en', name: context.mainDescription.en || '' }
          ].filter(desc => desc.name && desc.name.trim() !== ''),
          dateCreate: new Date().toISOString(),
          dateUpdate: new Date().toISOString(),
          contextId: context.id
        };
        
        if (descriptionType === 1) {
          mainDescriptionObj.contexts = contextsArray;
        }
        
        descriptions.push(mainDescriptionObj);
      }
      
      if (hasAdditionalDesc) {
        const additionalDescriptionObj: any = {
          descriptionType: descriptionType,
          position: 1,
          description: [
            { lang: 'ru', name: context.additionalDescription.ru || '' },
            { lang: 'en', name: context.additionalDescription.en || '' }
          ].filter(desc => desc.name && desc.name.trim() !== ''),
          dateCreate: new Date().toISOString(),
          dateUpdate: new Date().toISOString(),
          contextId: context.id
        };
        
        if (descriptionType === 1) {
          additionalDescriptionObj.contexts = contextsArray;
        }
        
        descriptions.push(additionalDescriptionObj);
      }
      
      if (!hasMainDesc && !hasAdditionalDesc) {
        const emptyMainDescriptionObj: any = {
          descriptionType: descriptionType,
          position: 0,
          description: [],
          dateCreate: new Date().toISOString(),
          dateUpdate: new Date().toISOString(),
          contextId: context.id
        };
        
        if (descriptionType === 1) {
          emptyMainDescriptionObj.contexts = contextsArray;
        }
        
        descriptions.push(emptyMainDescriptionObj);
        
        const emptyAdditionalDescriptionObj: any = {
          descriptionType: descriptionType,
          position: 1,
          description: [],
          dateCreate: new Date().toISOString(),
          dateUpdate: new Date().toISOString(),
          contextId: context.id
        };
        
        if (descriptionType === 1) {
          emptyAdditionalDescriptionObj.contexts = contextsArray;
        }
        
        descriptions.push(emptyAdditionalDescriptionObj);
      }
    });

    const canonicalInfo = {
      isCanonical: canonicalTags.length > 0,
      contextInfos: canonicalTags.map(domain => ({
        domain: domain || '',
        canonicalType: 0
      }))
    };


    const payload = {
      tagId: tagId || '',
      canonicalInfo,
      descriptions: contexts.length > 0 ? descriptions : []
    };

    try {
      const response = await API.request()
        .endpoint(Endpoints.tagsMnePw)
        .url('/api/Tags/UpdateCanonical')
        .addQuery('tagId', tagId || '')
        .post()
        .body(payload)
        .send();
      
      if (response?.status === 200) {
        toasts.info(t('common.success.data_saved'));
      } else {
        toasts.error(t('common.error.saving_data'));
      }
    } catch (error) {
      toasts.error(t('common.error.saving_data'));
    }
  };

  return (
    <>
    <ModalContainer style={{ overflow: 'auto', height: '100%' }} justify="flex-start" offsetTop={88} offsetTopMobile={1}>
      <ModalInnerContainer
        width="100%"
        directionMobile="column"
        justify={isMobile ? 'flex-start' : 'center'}
        align={isMobile ? 'center' : 'flex-start'}
        style={{ position: 'relative' }}
      >
        <ActionsWrapper width="100%" direction="column" style={{ justifyContent: 'center', position: 'relative' }}>
          <ActionsTitle justify="space-between" justifyMobile="flex-start" direction="row">
            <FlexBox direction="column">
              <MainTitle>{name || <Skeleton width={100} />}</MainTitle>
            </FlexBox>
            {!is744BP && (
              <FlexBox>
                <SendProjectButton isGreen={isGeo} width={187} onClick={handleSave}>
                  {t('competition.save')}
                </SendProjectButton>
              </FlexBox>
            )}
          </ActionsTitle>

          <FlexBox width="100%" gx={24} style={{ marginTop: 24 }}>
            <CanonicalDomainHeader>
              <SubTitle>{t('tags_modal.tag_description.canonical_domain')}</SubTitle>
              <div style={{marginLeft: 8}}>
                <InfoIcon msg={t('tags_modal.tag_description.canonical_domain_hint')} stc/>
              </div>
            </CanonicalDomainHeader>
            <FlexBox width="100%" direction="row" gx={12} align="center" style={{ flexWrap: 'wrap', gap: '8px' }}>
              {canonicalTags.map((tag) => (
                <HintTooltip
                  key={tag}
                  text={tag.length > 24 ? tag : ''}
                  customStyle={getTooltipStyles(theme)}
                  z={6}
                  noDisappearDelay
                  positionY="top"
                  onWhiteBg
                >
                  <BTag
                    showRemoveIcon
                    onIconClick={() => removeCanonicalTag(tag)}
                  >
                    {tag}
                  </BTag>
                </HintTooltip>
              ))}
              <BTag
                onClick={addCanonicalTag}
              >
                <AddIcon />
              </BTag>
            </FlexBox>
          </FlexBox>

          {contexts.map((context) => (
            <FlexBox key={context.id} width="100%" offsetTop={32}  directionMobile="column" style={{borderBottom: '1px solid #BFBFBF', paddingBottom: 32, columnGap: 24}}>
              <ContextLeftColumn>
                <SubTitle style={{marginTop: 6}}>{t('tags_modal.tag_description.context')}</SubTitle>
                <ContextTagsContainer>
                  {context.selectedApp && context.selectedApp.trim() !== '' ? (
                    (() => {
                      let contextsToShow: string[] = [];
                      
                      if (context.selectedApp === 'general:general') {
                        contextsToShow = ['Общее'];
                      } else {
                        try {
                          const parsed = JSON.parse(context.selectedApp);
                          if (Array.isArray(parsed)) {
                            contextsToShow = parsed.map((ctx: any) => {
                              return getContextUrl(ctx.appName, ctx.context);
                            });
                          } else {
                            contextsToShow = [context.contextUrl || 'Общее'];
                          }
                        } catch {
                          contextsToShow = [context.contextUrl || 'Общее'];
                        }
                      }
                      
                      return contextsToShow.map((contextName, index) => (
                        <HintTooltip
                          key={`${context.id}-${index}`}
                          text={contextName.length > 24 ? contextName : ''}
                          customStyle={getTooltipStyles(theme)}
                          z={6}
                          noDisappearDelay
                          positionY="top"
                          onWhiteBg
                        >
                          <BTag
                            showRemoveIcon
                            onIconClick={() => {
                              if (contextsToShow.length === 1) {
                                updateContext(context.id, 'selectedApp', '');
                                updateContext(context.id, 'contextUrl', '');
                              } else {
                                try {
                                  const parsed = JSON.parse(context.selectedApp);
                                  if (Array.isArray(parsed)) {
                                    const updatedContexts = parsed.filter((ctx: any, ctxIndex: number) => ctxIndex !== index);
                                    if (updatedContexts.length === 0) {
                                      updateContext(context.id, 'selectedApp', '');
                                      updateContext(context.id, 'contextUrl', '');
                                    } else if (updatedContexts.length === 1) {
                                      const ctx = updatedContexts[0];
                                      updateContext(context.id, 'selectedApp', `${ctx.appName}:${ctx.context}`);
                                      updateContext(context.id, 'contextUrl', getContextUrl(ctx.appName, ctx.context));
                                    } else {
                                      updateContext(context.id, 'selectedApp', JSON.stringify(updatedContexts));
                                      updateContext(context.id, 'contextUrl', updatedContexts.map((ctx: any) => getContextUrl(ctx.appName, ctx.context)).join(', '));
                                    }
                                  }
                                } catch {
                                  updateContext(context.id, 'selectedApp', '');
                                  updateContext(context.id, 'contextUrl', '');
                                }
                              }
                            }}
                          >
                            {contextName}
                          </BTag>
                        </HintTooltip>
                      ));
                    })()
                  ) : null}
                    <BTag onClick={() => {
                      setCurrentContextId(context.id);
                      setIsContextModalOpen(true);
                    }} >
                      <AddIcon />
                    </BTag>
                </ContextTagsContainer>
                <DeleteButtonContainer>  
                  <HintTooltip 
                    customStyle={getTooltipStyles(theme)} 
                    text={t('project.hints.delete_context')} 
                    z={6} 
                    noDisappearDelay 
                    positionY="top"
                    onWhiteBg
                  >
                    <BasketDeleteIcon onClick={() => removeContext(context.id)} fill="#909090"/>
                  </HintTooltip>
                </DeleteButtonContainer>
              </ContextLeftColumn>

              <FlexBox width="100%" direction="column" gy={24} style={{ flex: 1 }}>
                <InputWrapper width="100%" direction="column" gy={12} style={{ position: 'relative' }}>
                  <FlexBox width="100%" justify="space-between" align="center">
                    <SubTitle>{t('tags_modal.tag_description.main_description')}</SubTitle>
                    <LangComponent lang={context.currentLang} onLangChange={() => updateContext(context.id, 'currentLang', context.currentLang === 'ru' ? 'en' : 'ru')} />
              </FlexBox>
              <Input.Textarea
                      value={context.mainDescription[context.currentLang]}
                      onChange={e => updateContextDescription(context.id, context.currentLang, (e.target as any).value)}
                maxLength={500}
                      placeholder={t('tags_modal.tag_description.enter_description')}
              />
              <TextAreaLengthCounter
                      value={context.mainDescription[context.currentLang].length}
                pos={{ bottom: 8, right: isMobile ? 16 : 8 }}
                maxLength={500}
              />
            </InputWrapper>

                  <InputWrapper width="100%" direction="column" gy={12} style={{ position: 'relative' }}>
              <FlexBox width="100%" justify="space-between" align="center">
                      <SubTitle>{t('tags_modal.tag_description.additional_description')}</SubTitle>
                      <LangComponent 
                        lang={context.currentLangAdditional} 
                        onLangChange={() => updateContext(context.id, 'currentLangAdditional', context.currentLangAdditional === 'ru' ? 'en' : 'ru')} 
                      />
              </FlexBox>
              <Input.Textarea
                      value={context.additionalDescription[context.currentLangAdditional]}
                      onChange={e => updateContextAdditionalDescription(context.id, context.currentLangAdditional, (e.target as any).value)}
                maxLength={1500}
                      placeholder={t('tags_modal.tag_description.enter_additional_description')}
              />
              <TextAreaLengthCounter
                      value={context.additionalDescription[context.currentLangAdditional].length}
                pos={{ bottom: 8, right: isMobile ? 16 : 8 }}
                maxLength={1500}
              />
              </InputWrapper>
                </FlexBox>
            </FlexBox>
          ))}

          <FlexBox width="100%" align="center" style={{ marginTop: 24, marginBottom: 56 }}>
            <TagEditButton
              width={278}
              widthMobile={211}
              onClick={addContext}
              style={{columnGap: 8}}
            >
              <AddIcon fill={theme.tags.tagEditModal.buttons.edit.color}/>
              {t('tags_modal.tag_description.add_context')}
            </TagEditButton>
          </FlexBox>

          {is744BP && (
            <ActionBottomButtons>
              <SendProjectButton isGreen={isGeo} width={187} onClick={handleSave}>
                {t('competition.save')}
              </SendProjectButton>
            </ActionBottomButtons>
          )}
        </ActionsWrapper>
      </ModalInnerContainer>
    </ModalContainer>

    {isCanonicalDomainModalOpen && (
      <CanonicalDomainModal
        onClose={() => setIsCanonicalDomainModalOpen(false)}
        onSave={handleCanonicalDomainsSave}
        selectedDomains={canonicalTags}
      />
    )}

    {isContextModalOpen && (
      <ContextModal
        onClose={() => {
          setIsContextModalOpen(false);
          setCurrentContextId('');
        }}
        onSave={handleContextsSave}
        availableContexts={availableContexts}
        selectedContexts={(() => {
          if (!currentContextId) return [];
          
          const currentContext = contexts.find(ctx => ctx.id === currentContextId);
          if (!currentContext || !currentContext.selectedApp) return [];
          
          try {
            const parsed = JSON.parse(currentContext.selectedApp);
            if (Array.isArray(parsed)) {
              return parsed.map((ctx: any) => ({
                appName: ctx.appName || 'general',
                context: ctx.context || 'general',
                url: ctx.url || ctx.appName
              }));
            } else {
              return [{
                appName: parsed.appName || 'general',
                context: parsed.context || 'general',
                url: parsed.url || parsed.appName
              }];
            }
          } catch {
            const [appName, contextValue] = currentContext.selectedApp.includes(':') 
              ? currentContext.selectedApp.split(':') 
              : [currentContext.selectedApp, currentContext.selectedApp];
            const url = currentContext.contextUrl || currentContext.selectedApp;
            
            return [{
              appName: appName,
              context: contextValue,
              url: url
            }];
          }
        })()}
        usedContexts={(() => {
          return contexts
            .filter(ctx => ctx.id !== currentContextId)
            .map(ctx => {
              if (!ctx.selectedApp) return [];
              
              try {
                const parsed = JSON.parse(ctx.selectedApp);
                if (Array.isArray(parsed)) {
                  return parsed.map((c: any) => `${c.appName}:${c.context}`);
                }
              } catch {
                return [ctx.selectedApp];
              }
              return [ctx.selectedApp];
            })
            .flat();
        })()}
      />
    )}
    </>
  );
};
